<template>
  <div class="m-2">
    <div class="mainheader d-flex justify-content-left p-2">
      <span>Patmatch</span>
      <img
        src="@/assets/diamond.svg"
        height="20px"
        alt="Premium"
        class="ml-2"
      />
    </div>

    <div class="pl-2 text-left">
      <p>
        Pattern Matching allows you to search for short (&lt;20 residues)
        nucleotide or peptide sequences, or ambiguous/degenerate patterns. It
        uses the same Arabidopsis dataset as TAIR's
        <a href="https://www.arabidopsis.org/tools/blast/">BLAST</a>. If you are
        searching for a sequence &gt;20 bp or aa with no degenerate positions,
        please use BLAST, which is much faster. Pattern Matching allows for
        ambiguous characters, mismatches, insertions and deletions, but does not
        do alignments and so is not a replacement for BLAST. Currently the
        maximum number of hits retrieved is 250,000 and the minimum number of
        input string is 3 residues.
      </p>
      <p>
        <a
          href="https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217191/PatMatch+Notes"
          target="_blank"
          >Version 1.1 Release Notes</a
        >
      </p>
    </div>

    <div class="container p-2 border rounded shadow-sm form-bg">
      <form @submit.prevent="handleSubmit" @reset.prevent="handleReset">
        <!-- Sequence Input -->
        <fieldset class="border border-dark rounded p-2">
          <span class="pl-4 mb-3 d-flex justify-content-left">
            <b>Enter a sequence or pattern</b> &nbsp;(<a href="#syntax"
              >examples</a
            >)
          </span>
          <div class="form-group row">
            <label for="seq_class" class="col-sm-4 col-form-label text-left"
              >Sequence Type:</label
            >
            <div class="col-sm-4">
              <select
                id="seq_class"
                v-model="form.seq_class"
                class="form-control"
                @change="onSequenceTypeChange"
              >
                <option value="nucleotide">Nucleotide</option>
                <option value="peptide">Peptide</option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="pattern" class="col-sm-4 col-form-label text-left"
              >Sequence or Pattern:</label
            >
            <div class="col-sm-8">
              <input
                id="pattern"
                v-model="form.pat"
                class="form-control"
                placeholder="Enter sequence or pattern"
                required
              />
            </div>
          </div>
        </fieldset>

        <!-- Database Selection -->
        <fieldset class="border border-dark rounded p-2 mt-4">
          <span class="pl-4 mb-3 d-flex justify-content-left">
            <b>Choose a Sequence Database:</b>
          </span>
          <p class="pl-4 text-left">
            All public Arabidopsis sequences can be found within these datasets.
            (<a
              href="https://phoenixbioinformatics.atlassian.net/wiki/spaces/COM/pages/42217028/TAIR+BLAST+Help#TAIRBLASTHelp-TAIRDatasets"
              target="_blank"
              >Datasets Description</a
            >)
          </p>
          <div class="form-group row">
            <label for="current_db" class="col-sm-4 col-form-label text-left"
              >Database:</label
            >
            <div class="col-sm-8">
              <!-- Nucleotide database select -->
              <select
                id="nucleotide_db"
                v-model="form.current_db"
                class="form-control"
                v-show="form.seq_class === 'nucleotide'"
              >
                <option
                  v-for="option in nucleotideDatabaseOptions"
                  :key="option.value"
                  :value="option.value"
                  :disabled="option.disabled"
                >
                  {{ option.text }}
                </option>
              </select>

              <!-- Peptide database select -->
              <select
                id="peptide_db"
                v-model="form.current_db"
                class="form-control"
                v-show="form.seq_class === 'peptide'"
              >
                <option
                  v-for="option in peptideDatabaseOptions"
                  :key="option.value"
                  :value="option.value"
                  :disabled="option.disabled"
                >
                  {{ option.text }}
                </option>
              </select>
            </div>
          </div>
        </fieldset>

        <!-- Advanced Options -->
        <fieldset class="border border-dark rounded p-2 mt-4">
          <span class="pl-4 mb-3 d-flex justify-content-left">
            <b>More Options:</b>
          </span>
          <div class="form-group row">
            <label for="max_hits" class="col-sm-4 col-form-label text-left"
              >Maximum Hits:</label
            >
            <div class="col-sm-4">
              <select
                id="max_hits"
                v-model="form.max_hits"
                class="form-control"
              >
                <option
                  v-for="hits in maxHitsOptions"
                  :key="hits"
                  :value="hits"
                >
                  {{ hits }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="strand" class="col-sm-4 col-form-label text-left"
              >DNA Strand:</label
            >
            <div class="col-sm-4">
              <select id="strand" v-model="form.strand" class="form-control">
                <option value="both">Both strands</option>
                <option value="Watson">Watson (given)</option>
                <option value="Crick">Crick (reverse complement)</option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label for="mismatch" class="col-sm-4 col-form-label text-left"
              >Mismatch:</label
            >
            <div class="col-sm-4">
              <select
                id="mismatch"
                v-model="form.mismatch"
                class="form-control"
                @change="updateMismatchCheckboxes"
              >
                <option
                  v-for="mismatch in mismatchOptions"
                  :key="mismatch"
                  :value="mismatch"
                >
                  {{ mismatch }}
                </option>
              </select>
            </div>
          </div>
          <div class="form-group row">
            <label class="col-sm-4 col-form-label text-left"
              >Mismatch Type:</label
            >
            <div class="col-sm-8">
              <div class="form-check text-left">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="insertion"
                  v-model="form.insertion"
                  @change="updateMismatchDropdown"
                />
                <label for="insertion" class="form-check-label"
                  >Insertions</label
                >
              </div>
              <div class="form-check text-left">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="deletion"
                  v-model="form.deletion"
                  @change="updateMismatchDropdown"
                />
                <label for="deletion" class="form-check-label">Deletions</label>
              </div>
              <div class="form-check text-left">
                <input
                  class="form-check-input"
                  type="checkbox"
                  id="substitution"
                  v-model="form.substitution"
                  @change="updateMismatchDropdown"
                />
                <label for="substitution" class="form-check-label"
                  >Substitutions</label
                >
              </div>
            </div>
          </div>
          <div class="form-group row">
            <label for="min_seq_hits" class="col-sm-4 col-form-label text-left"
              >Minimum Hits per Sequence:</label
            >
            <div class="col-sm-4">
              <input
                type="number"
                id="min_seq_hits"
                v-model="form.min_seq_hits"
                class="form-control"
                min="1"
                max="99999"
              />
            </div>
          </div>
          <div class="form-group row">
            <label for="max_seq_hits" class="col-sm-4 col-form-label text-left"
              >Maximum Hits per Sequence:</label
            >
            <div class="col-sm-4">
              <input
                type="number"
                id="max_seq_hits"
                v-model="form.max_seq_hits"
                class="form-control"
                min="1"
                max="99999"
              />
            </div>
          </div>
        </fieldset>

        <!-- Submit and Reset Buttons -->
        <div class="form-group row">
          <div class="col-sm-4 offset-sm-1 d-flex justify-content-left mt-2">
            <button type="submit" class="btn btn-primary">
              Start Pattern Search
            </button>
            <button type="reset" class="ml-4 btn btn-secondary">Reset</button>
          </div>
        </div>
      </form>

      <!-- Pattern Syntax Reference -->
      <div class="mt-4">
        <a name="syntax"></a>
        <h3 class="mb-3">Supported Pattern Syntax and Examples:</h3>
        <div class="table-responsive">
          <table class="table table-bordered">
            <thead>
              <tr>
                <th>Search Type</th>
                <th>Character</th>
                <th>Meaning</th>
                <th>Examples</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th rowspan="6">Peptide Searches</th>
                <td>IFVLWMAGCYP TSHEDQNKR</td>
                <td>Exact match</td>
                <td>DQGT</td>
              </tr>
              <tr>
                <td>J</td>
                <td>Any hydrophobic residue (IFVLWMAGCY)</td>
                <td>AAAAAAJJ</td>
              </tr>
              <tr>
                <td>O</td>
                <td>Any hydrophilic residue (TSHEDQNKR)</td>
                <td>TTTTTTOO</td>
              </tr>
              <tr>
                <td>B</td>
                <td>D or N</td>
                <td>FLGB</td>
              </tr>
              <tr>
                <td>Z</td>
                <td>E or Q</td>
                <td>GLFGZ</td>
              </tr>
              <tr>
                <td>X or .</td>
                <td>Any amino acid</td>
                <td>DXXXNW..VSK</td>
              </tr>
              <tr>
                <th rowspan="12">Nucleotide searches</th>
                <td>ACTGU</td>
                <td>Exact match</td>
                <td>ACCGGCGTAA</td>
              </tr>
              <tr>
                <td>R</td>
                <td>Any purine base (AG)</td>
                <td>AAGGCCGGRRRR</td>
              </tr>
              <tr>
                <td>Y</td>
                <td>Any pyrimidine base (CT)</td>
                <td>CCCATAYYGGYY</td>
              </tr>
              <tr>
                <td>S</td>
                <td>G or C</td>
                <td rowspan="4">YGGTWCAMWTGTY</td>
              </tr>
              <tr>
                <td>W</td>
                <td>A or T</td>
              </tr>
              <tr>
                <td>M</td>
                <td>A or C</td>
              </tr>
              <tr>
                <td>K</td>
                <td>G or T</td>
              </tr>
              <tr>
                <td>V</td>
                <td>A or C or G</td>
                <td rowspan="4">CCGG...WHW.{3,5}HWH...CCGG</td>
              </tr>
              <tr>
                <td>H</td>
                <td>A or C or T</td>
              </tr>
              <tr>
                <td>D</td>
                <td>A or G or T</td>
              </tr>
              <tr>
                <td>B</td>
                <td>C or G or T</td>
              </tr>
              <tr>
                <td>N or X or .</td>
                <td>Any base</td>
                <td>ATGCTNNNNATCG</td>
              </tr>
              <tr>
                <th rowspan="6">All searches:</th>
                <td>[ ]</td>
                <td>A subset of elements<br />[TC] = T or C</td>
                <td>[WFY]XXXDN[RK][ST]</td>
              </tr>
              <tr>
                <td>[^ ]</td>
                <td>An excluded subset of elements<br />[^TA] = not T or A</td>
                <td>NDBB...[VILM]Z[DE]...[^PG]</td>
              </tr>
              <tr>
                <td>( )</td>
                <td>Specifies a sub-pattern</td>
                <td>(YDXXX){2,}</td>
              </tr>
              <tr>
                <td>{m,n}</td>
                <td>
                  {m} = exactly m times<br />{m,} = at least m times<br />{,m} =
                  0 to m times
                </td>
                <td>L{3,5}X{5}DGZ</td>
              </tr>
              <tr>
                <td>&lt;</td>
                <td>Constrains pattern to N-terminus or 5' end</td>
                <td>&lt;MNTD (pep)<br />&lt;ATGX{6,10}RTTRTT (nuc)</td>
              </tr>
              <tr>
                <td>&gt;</td>
                <td>Constrains pattern to C-terminus or 3' end</td>
                <td>SBGZ&gt; (pep)<br />YATTRTGA&gt; (nuc)</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  data() {
    return {
      form: {
        seq_class: "nucleotide",
        pat: "",
        current_db: "",
        max_hits: "75000",
        strand: "both",
        mismatch: "0",
        insertion: false,
        deletion: false,
        substitution: false,
        min_seq_hits: "1",
        max_seq_hits: "100",
      },
      nucleotideDatabaseOptions: [
        {
          text: "Araport11 3' UTRs (DNA)",
          value: "Araport11_3_utr_20220914",
        },
        {
          text: "Araport11 5' UTRs (DNA)",
          value: "Araport11_5_utr_20220914",
        },
        {
          text: "Araport11 transcripts (DNA)",
          value: "Araport11_cdna_20220914",
        },
        {
          text: "Araport11 coding sequences (DNA)",
          value: "Araport11_cds_20220914",
        },
        {
          text: "Araport11 Loci Downstream Seq - 1000bp (DNA)",
          value: "Araport11_downstream_1000_20220914",
        },
        {
          text: "Araport11 Loci Downstream Seq - 3000bp (DNA)",
          value: "Araport11_downstream_3000_20220914",
        },
        {
          text: "Araport11 Loci Downstream Seq - 500bp (DNA)",
          value: "Araport11_downstream_500_20220914",
        },
        { text: "-------------", value: "separator1", disabled: true },
        {
          text: "Araport11 Intergenic Sequences (DNA)",
          value: "Araport11_intergenic_20220914",
        },
        { text: "-------------", value: "separator2", disabled: true },
        {
          text: "Araport11 Intron Sequences (DNA)",
          value: "Araport11_intron_20220914",
        },
        { text: "-------------", value: "separator3", disabled: true },
        {
          text: "Araport11 genomic locus sequences (DNA)",
          value: "Araport11_seq_20220914",
        },
        {
          text: "Araport11 Loci Upstream Seq - 1000bp (DNA)",
          value: "Araport11_upstream_1000_20220914",
        },
        {
          text: "Araport11 Loci Upstream Seq - 3000bp (DNA)",
          value: "Araport11_upstream_3000_20220914",
        },
        {
          text: "Araport11 Loci Upstream Seq - 500bp (DNA)",
          value: "Araport11_upstream_500_20220914",
        },
        /*{ text: "-------------", value: "separator4", disabled: true },
        {
          text: "TAIR10 Transcripts (-introns, +UTRs) (DNA)",
          value: "At_transcripts",
        },
        {
          text: "TAIR10 CDS (-introns, -UTRs) (DNA)",
          value: "ATH1_cds",
        },
        {
          text: "TAIR10 Genes (+introns, +UTRs) (DNA)",
          value: "ATH1_seq",
        },
        {
          text: "TAIR10 Whole Genome (BAC clones) (DNA)",
          value: "ATH1_bacs_con",
        },
        {
          text: "TAIR10 Loci Upstream Sequences -- 500 bp (DNA)",
          value: "At_upstream_500",
        },
        {
          text: "TAIR10 Loci Upstream Sequences -- 1000 bp (DNA)",
          value: "At_upstream_1000",
        },
        {
          text: "TAIR10 Loci Upstream Sequences -- 3000 bp (DNA)",
          value: "At_upstream_3000",
        },
        {
          text: "TAIR10 Loci Downstream Sequences -- 1000 bp (DNA)",
          value: "At_downstream_1000",
        },
        {
          text: "TAIR10 Loci Downstream Sequences -- 3000 bp (DNA)",
          value: "At_downstream_3000",
        },
        {
          text: "TAIR10 Intergenic (DNA)",
          value: "At_intergenic",
        },
        { text: "TAIR10 Intron (DNA)", value: "At_intron" },
        {
          text: "TAIR10 5' UTRs (DNA)",
          value: "ATH1_5_UTR",
        },
        {
          text: "TAIR10 3' UTRs (DNA)",
          value: "ATH1_3_UTR",
        },
        { text: "-------------", value: "separator5", disabled: true },
        {
          text: "A. thaliana Insertion Flanks (DNA)",
          value: "At_flanks_DNA",
        },
        {
          text: "A. thaliana GB experimental cDNA/EST (DNA)",
          value: "At_GB_exp_tx_DNA",
        },
        {
          text: "A. thaliana GB refseq/tpa cDNA (DNA)",
          value: "At_GB_refseq_tx_DNA",
        },
        {
          text: "A. thaliana GB genomic (DNA)",
          value: "At_GB_genomic_DNA",
        },
        { text: "-------------", value: "separator6", disabled: true },
        {
          text: "Green plant GB refseq/tpa cDNA (DNA)",
          value: "gp_GB_refseq_tx_DNA",
        },*/
      ],
      peptideDatabaseOptions: [
        {
          text: "Araport11 protein sequences (protein)",
          value: "Araport11_pep_20220914",
        },
        /*{ text: "-------------", value: "separator7", disabled: true },
        {
          text: "TAIR10 Proteins (protein)",
          value: "ATH1_pep",
        },
        { text: "-------------", value: "separator8", disabled: true },
        {
          text: "A. thaliana UniProt (protein)",
          value: "At_Uniprot_prot",
        },
        {
          text: "A. thaliana GB derived from mRNA (protein)",
          value: "At_GB_exp_prot",
        },
        {
          text: "A. thaliana GB refseq/tpa (protein)",
          value: "At_GB_refseq_prot",
        },
        {
          text: "A. thaliana GB all (protein)",
          value: "At_GB_all_prot",
        },
        { text: "-------------", value: "separator9", disabled: true },
        {
          text: "Green plant GB derived from mRNA (protein)",
          value: "gp_GB_exp_prot",
        },
        {
          text: "Green plant GB refseq/tpa (protein)",
          value: "gp_GB_refseq_prot",
        },
        {
          text: "Green plant GB all (protein)",
          value: "gp_GB_all_prot",
        }*/
      ],
      maxHitsOptions: [
        25, 50, 100, 200, 500, 1000, 75000, 100000, 150000, 200000, 250000,
      ],
      mismatchOptions: ["0", "1", "2", "3"],
    };
  },
  computed: {
    // No longer needed with the two-select approach
  },
  watch: {
    "form.seq_class": function (newVal) {
      console.log("Sequence type changed to:", newVal);
    },
  },
  methods: {
    updateMismatchCheckboxes() {
      if (
        this.form.mismatch !== "0" &&
        !this.form.insertion &&
        !this.form.deletion &&
        !this.form.substitution
      ) {
        this.form.insertion = true;
        this.form.deletion = true;
        this.form.substitution = true;
      }
      if (this.form.mismatch === "0") {
        this.form.insertion = false;
        this.form.deletion = false;
        this.form.substitution = false;
      }
    },
    updateMismatchDropdown() {
      if (
        this.form.mismatch === "0" &&
        (this.form.insertion || this.form.deletion || this.form.substitution)
      ) {
        this.form.mismatch = "1";
      }
      if (
        this.form.mismatch !== "0" &&
        !this.form.insertion &&
        !this.form.deletion &&
        !this.form.substitution
      ) {
        this.form.mismatch = "0";
      }
    },
    handleSubmit() {
      // Form submission logic here
      console.log("Form submitted with data:", this.form);
      const actionUrl = `${process.env.VUE_APP_TOOLS_BASE_URL}/cgi-bin/patmatch/nph-patmatch.pl`;
      const method = "POST";
      const target = "_blank";

      // Map strand values to what the backend expects
      let strandValue;
      switch (this.form.strand) {
        case "Watson":
          strandValue = "Watson";
          break;
        case "Crick":
          strandValue = "Crick";
          break;
        case "both":
        default:
          strandValue = "both strands";
          break;
      }

      const fields = [
        {
          type: "hidden",
          name: "default_db",
          value: this.form.current_db,
        },
        {
          type: "hidden",
          name: "current_db",
          value: this.form.current_db,
        },
        {
          type: "hidden",
          name: "seq_class",
          value: this.form.seq_class,
        },
        {
          type: "hidden",
          name: "pat",
          value: this.form.pat,
        },
        {
          type: "hidden",
          name: "strand",
          value: strandValue,
        },
        {
          type: "hidden",
          name: "maxhits",
          value: this.form.max_hits,
        },
        {
          type: "hidden",
          name: "mismatch",
          value: this.form.mismatch,
        },
        //min_seq_hits
        {
          type: "hidden",
          name: "min_seq_hits",
          value: this.form.min_seq_hits,
        },
        //max_seq_hits
        {
          type: "hidden",
          name: "max_seq_hits",
          value: this.form.max_seq_hits,
        },
        {
          type: "hidden",
          name: "insertion",
          value: this.form.insertion === true ? "on" : "off",
        },
        {
          type: "hidden",
          name: "deletion",
          value: this.form.deletion === true ? "on" : "off",
        },
        {
          type: "hidden",
          name: "substitution",
          value: this.form.substitution === true ? "on" : "off",
        },
      ];
      this.submitForm(actionUrl, method, target, fields);
    },
    submitForm(actionUrl, method, target, fields) {
      console.log("Submitting form with fields:", fields);
      console.log("Selected database:", this.form.current_db);
      console.log("Sequence type:", this.form.seq_class);

      // Create a form element
      const form = document.createElement("form");
      form.method = method;
      form.action = actionUrl;
      form.target = target;

      // Add form fields
      fields.forEach((field) => {
        const input = document.createElement("input");
        input.type = field.type;
        input.name = field.name;
        input.value = field.value;
        form.appendChild(input);
      });

      // Append the form to the body
      document.body.appendChild(form);

      // Submit the form
      form.submit();

      // Remove the form from the body after submission
      document.body.removeChild(form);
    },
    handleReset() {
      this.form = {
        seq_class: "nucleotide",
        pat: "",
        current_db: "",
        max_hits: "75000",
        strand: "both",
        mismatch: "0",
        insertion: false,
        deletion: false,
        substitution: false,
        min_seq_hits: "1",
        max_seq_hits: "100",
      };
      this.onSequenceTypeChange();
    },
    onSequenceTypeChange() {
      console.log("Sequence type changed to:", this.form.seq_class);

      // Set default database option based on sequence type
      if (this.form.seq_class === "peptide") {
        // Find first non-disabled peptide option
        const firstValidOption = this.peptideDatabaseOptions.find(
          (option) => !option.disabled && option.value !== ""
        );
        if (firstValidOption) {
          this.form.current_db = firstValidOption.value;
        }
      } else {
        // Find first non-disabled nucleotide option
        const firstValidOption = this.nucleotideDatabaseOptions.find(
          (option) => !option.disabled && option.value !== ""
        );
        if (firstValidOption) {
          this.form.current_db = firstValidOption.value;
        }
      }
    },
  },
  mounted() {
    console.log("Component mounted");
    // Initialize the database selection based on current sequence type
    this.onSequenceTypeChange();
  },
};
</script>

<style scoped>
.mainheader {
  font-size: 16pt;
  font-weight: bold;
  color: #930;
}
.container {
  max-width: 100%;
}
.form-bg {
  background: #f1efec;
}
fieldset {
  margin-bottom: 1rem;
}
.table th {
  background-color: #f8f9fa;
}
</style>
